import { useQuery, useQueryClient } from "@tanstack/react-query";

import type { Address } from "viem";
import type { RedKeel } from "../config/networks";
import { clienteLogs } from "../lib/cliente";
import { eventosDeVault, soloDe } from "./useEventos";


export type Actividad = {
  /** cuando se abrio la posicion, en segundos */
  abierta?: number;
  dias: number;
  reajustes: number;
  /** de los reajustes, cuantos abrieron rango de recuperacion */
  recuperaciones: number;
  /** de las recuperaciones, cuantas se toparon con el limite de estiramiento */
  topadas: number;
  barridos: number;
  reinyecciones: number;
  /** media de horas entre reajustes */
  horasEntre?: number;
  ultimoReajuste?: number;
};

/**
 * Que ha hecho el robot desde que se abrio la piscina.
 *
 * Los numeros sueltos del resumen no dicen si esta trabajando bien. Que haya 11 reajustes en 4
 * dias significa un rango demasiado estrecho para ese activo; que 5 de 6 abrieran rango de
 * recuperacion significa que el precio lleva cayendo. Eso es lo accionable.
 */
export function useActividad(vault: Address | null, red: RedKeel | undefined) {
  const qc = useQueryClient();

  return useQuery({
    queryKey: ["actividad", red?.clave, vault],
    enabled: !!vault && !!red,
    staleTime: 60_000,
    queryFn: async (): Promise<Actividad> => {
      // Eventos: por `rpcLogs` donde lo hay. Ver la nota de `clienteLogs`.
      const client = clienteLogs(red!);

      // TODOS de una vez y filtrados aquí, en vez de una consulta por evento desde el bloque 0.
      // Aquello no se podía trocear —serían cinco consultas por ventana— y en BNB Chain, que no
      // acepta el rango entero, fallaban las cinco: el panel del robot decía «0 reajustes» en una
      // piscina que llevaba días trabajando. Comparte consulta con el historial y el gas.
      const logs = await eventosDeVault(qc, red!, vault!);
      const de = (n: string) => soloDe(logs, n);
      const [abiertas, reajustes, recuperaciones, barridos, reinyecciones] = [
        de("Opened"), de("Rebalanced"), de("Recovered"), de("Swept"), de("Reinjected"),
      ];

      // Solo dos fechas: la de apertura y la del ultimo reajuste. Pedir el bloque de cada evento
      // serian decenas de peticiones para calcular una media que se aproxima igual de bien con los
      // extremos y el numero de reajustes.
      const fecha = async (b?: bigint) => {
        if (b === undefined) return undefined;
        try { return Number((await client.getBlock({ blockNumber: b })).timestamp); }
        catch { return undefined; }
      };

      const bloqueApertura = abiertas[0]?.blockNumber;
      const bloqueUltimo = reajustes[reajustes.length - 1]?.blockNumber;
      const [abierta, ultimoReajuste] = await Promise.all([fecha(bloqueApertura), fecha(bloqueUltimo)]);

      const ahora = Math.floor(Date.now() / 1000);
      const dias = abierta ? (ahora - abierta) / 86400 : 0;

      return {
        abierta,
        dias,
        reajustes: reajustes.length,
        recuperaciones: recuperaciones.length,
        topadas: (recuperaciones as never as Array<{ args: { capped: boolean } }>)
          .filter((l) => l.args.capped).length,
        barridos: barridos.length,
        reinyecciones: reinyecciones.length,
        horasEntre: reajustes.length > 1 && abierta && ultimoReajuste
          ? ((ultimoReajuste - abierta) / 3600) / (reajustes.length - 1)
          : undefined,
        ultimoReajuste,
      };
    },
  });
}
